// 平台配置屏：全局开关 / 数值。表格化展示，「值」列可直接编辑后保存。
// code 列遵循 UPPER_SNAKE_CASE 风格，启停 active 控制是否对客户端可见。

import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import {
  api, errorMessage,
  type PlatformConfigRow, type PlatformConfigValueType,
  type PlatformConfigCreateInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useI18n } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

const TYPES: PlatformConfigValueType[] = ['string', 'number', 'boolean', 'json']

interface EditingForm {
  id: string | null
  code: string
  value: string
  valueType: PlatformConfigValueType
  description: string
  sortOrder: number
  active: 0 | 1
}

function emptyForm(): EditingForm {
  return { id: null, code: '', value: '', valueType: 'string', description: '', sortOrder: 0, active: 1 }
}
function rowToForm(r: PlatformConfigRow): EditingForm {
  return {
    id: r.id, code: r.code, value: r.value, valueType: r.valueType,
    description: r.description, sortOrder: r.sortOrder, active: r.active === 1 ? 1 : 0,
  }
}

function fmtTime(sec: number): string {
  if (!sec) return '—'
  const d = new Date(sec * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export function PlatformConfigScreen() {
  const toast = useToast()
  const { lang } = useI18n()
  const isZh = lang === 'zh'
  const [rows, setRows] = useState<PlatformConfigRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<EditingForm | null>(null)
  const [deleting, setDeleting] = useState<PlatformConfigRow | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  // 行内编辑：用户必须点「编辑」按钮进入编辑态；rowEditing Set 记录哪些行
  // 在编辑。draft 存草稿；点「保存」commit；点「取消」放弃。默认全是查看态。
  const [rowEditing, setRowEditing] = useState<Set<string>>(new Set())
  const [inlineDraft, setInlineDraft] = useState<Record<string, string>>({})
  function startRowEdit(r: PlatformConfigRow) {
    setRowEditing((s) => new Set(s).add(r.id))
    setInlineDraft((d) => ({ ...d, [r.id]: r.value }))
  }
  function cancelRowEdit(r: PlatformConfigRow) {
    setRowEditing((s) => { const n = new Set(s); n.delete(r.id); return n })
    setInlineDraft((d) => { const { [r.id]: _, ...rest } = d; return rest })
  }

  function load() {
    setBusy(true); setError(null)
    api.listPlatformConfig()
      .then((r) => setRows(r.items))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load() }, [])

  async function handleSave() {
    if (!editing) return
    if (!editing.code.match(/^[A-Z0-9_]+$/)) {
      toast.error(isZh ? 'code 必须为大写字母/数字/下划线' : 'code must be UPPER_SNAKE_CASE')
      return
    }
    setBusy(true)
    try {
      const input: PlatformConfigCreateInput = {
        code: editing.code, value: editing.value, valueType: editing.valueType,
        description: editing.description, sortOrder: editing.sortOrder, active: editing.active,
      }
      if (editing.id) {
        await api.patchPlatformConfig(editing.id, input)
        toast.success(isZh ? '已保存' : 'Saved')
      } else {
        await api.createPlatformConfig(input)
        toast.success(isZh ? '已创建' : 'Created')
      }
      setEditing(null)
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function commitInline(row: PlatformConfigRow) {
    const next = inlineDraft[row.id]
    if (next === undefined || next === row.value) {
      // 没改 — 直接退出编辑态
      cancelRowEdit(row)
      return
    }
    try {
      await api.patchPlatformConfig(row.id, { value: next })
      toast.success(isZh ? '已保存' : 'Saved')
      cancelRowEdit(row)
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  async function handleDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await api.deletePlatformConfig(deleting.id)
      toast.success(isZh ? '已删除' : 'Deleted')
      setDeleting(null)
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setDeleteBusy(false)
    }
  }

  async function toggleActive(r: PlatformConfigRow) {
    try {
      await api.patchPlatformConfig(r.id, { active: r.active === 1 ? 0 : 1 })
      load()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  if (rows === null && busy) return <PageLoading/>
  if (error) return <PageError msg={error}/>

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">{isZh ? '平台配置' : 'Platform config'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isZh
              ? '全局开关与数值（如返佣比例、订单超时等）。改 active=0 立即对客户端隐藏；客户端读取走 /shop/platform-config。'
              : 'Global switches and values. active=0 hides from public API immediately. Clients read via /shop/platform-config.'}
          </p>
        </div>
        <Button onClick={() => setEditing(emptyForm())}>
          <Plus className="h-4 w-4"/>
          {isZh ? '新建配置' : 'New config'}
        </Button>
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <THead>
            <Tr>
              {/* 描述列拿走最大空间。其它列全部 width-fixed 不参与 flex */}
              <Th className="w-10">{isZh ? '序号' : '#'}</Th>
              <Th className="w-[180px]">{isZh ? '配置编码' : 'Code'}</Th>
              <Th>{isZh ? '描述' : 'Description'}</Th>
              <Th className="w-24">{isZh ? '值' : 'Value'}</Th>
              <Th className="w-[120px]">{isZh ? '更新时间' : 'Updated at'}</Th>
              <Th className="w-24">{isZh ? '操作人' : 'Updated by'}</Th>
              <Th className="w-44"></Th>
            </Tr>
          </THead>
          <TBody>
            {(rows || []).map((r, i) => {
              const isEditing = rowEditing.has(r.id)
              const draft = inlineDraft[r.id] ?? r.value
              return (
                <Tr key={r.id}>
                  <Td className="text-muted-foreground tabular-nums align-top">{i + 1}</Td>
                  <Td className="align-top">
                    <div className="font-mono text-xs font-medium break-all">{r.code}</div>
                    {r.active === 0 && <Badge tone="default" className="mt-1">{isZh ? '已停用' : 'Inactive'}</Badge>}
                  </Td>
                  {/* 描述：xs 字号 + leading-relaxed，自然换行，不再强制纵向"一字一行" */}
                  <Td className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap break-words align-top">
                    {r.description}
                  </Td>
                  <Td className="align-top">
                    {!isEditing ? (
                      <span className="font-mono text-sm break-all">{r.value || <span className="text-muted-foreground">—</span>}</span>
                    ) : r.valueType === 'boolean' ? (
                      <select
                        autoFocus
                        className="bg-background border border-border rounded-md text-sm px-2 py-1 font-mono w-full"
                        value={draft}
                        onChange={(e) => setInlineDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      >
                        <option value="0">0</option>
                        <option value="1">1</option>
                      </select>
                    ) : (
                      <Input
                        autoFocus
                        value={draft}
                        type={r.valueType === 'number' ? 'number' : 'text'}
                        className="font-mono text-sm"
                        onChange={(e) => setInlineDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitInline(r) }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRowEdit(r) }
                        }}
                      />
                    )}
                  </Td>
                  {/* 更新时间：紧凑两行 (yyyy-mm-dd / hh:mm)，宽度只 ~120px 也够看 */}
                  <Td className="text-xs text-muted-foreground tabular-nums whitespace-nowrap align-top">
                    <div>{fmtTime(r.updatedAt).split(' ')[0]}</div>
                    <div className="text-muted-foreground/70">{fmtTime(r.updatedAt).split(' ')[1]}</div>
                  </Td>
                  <Td className="text-xs text-muted-foreground align-top truncate max-w-24">{r.updatedBy ?? '—'}</Td>
                  <Td className="text-right whitespace-nowrap align-top">
                    {isEditing ? (
                      <>
                        <Button variant="default" size="sm" onClick={() => commitInline(r)}>{isZh ? '保存' : 'Save'}</Button>
                        <Button variant="ghost" size="sm" onClick={() => cancelRowEdit(r)}>{isZh ? '取消' : 'Cancel'}</Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => startRowEdit(r)}>{isZh ? '编辑' : 'Edit'}</Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(rowToForm(r))}>{isZh ? '详情' : 'More'}</Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleActive(r)}>
                          {r.active ? (isZh ? '停用' : 'Disable') : (isZh ? '启用' : 'Enable')}
                        </Button>
                      </>
                    )}
                  </Td>
                </Tr>
              )
            })}
            {rows && rows.length === 0 && (
              <Tr><Td colSpan={7} className="text-center text-muted-foreground py-8">{isZh ? '暂无配置' : 'No configs yet'}</Td></Tr>
            )}
          </TBody>
        </Table>
      </div>

      {editing && (
        <EditDrawer
          form={editing} onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          busy={busy}
          isZh={isZh}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={isZh ? '删除配置？' : 'Delete config?'}
        message={deleting ? `${isZh ? '将删除' : 'About to delete'}: ${deleting.code}` : ''}
        confirmLabel={isZh ? '删除' : 'Delete'}
        cancelLabel={isZh ? '取消' : 'Cancel'}
        tone="danger"
        busy={deleteBusy}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}

interface EditDrawerProps {
  form: EditingForm
  onChange: (f: EditingForm) => void
  onClose: () => void
  onSave: () => void
  busy: boolean
  isZh: boolean
}
function EditDrawer({ form, onChange, onClose, onSave, busy, isZh }: EditDrawerProps) {
  const update = <K extends keyof EditingForm>(k: K, v: EditingForm[K]) => onChange({ ...form, [k]: v })
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold">{form.id ? (isZh ? '编辑配置' : 'Edit config') : (isZh ? '新建配置' : 'New config')}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4"/></Button>
        </div>
        <div className="p-4 space-y-4">
          <Field label={isZh ? '配置编码 *（UPPER_SNAKE，创建后不建议修改）' : 'Code * (UPPER_SNAKE, avoid changing after create)'}>
            <Input value={form.code} onChange={(e) => update('code', e.target.value.toUpperCase())} className="font-mono" placeholder="REFERRAL_REWARD_PCT"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={isZh ? '类型' : 'Type'}>
              <select className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full" value={form.valueType} onChange={(e) => update('valueType', e.target.value as PlatformConfigValueType)}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label={isZh ? '排序' : 'Sort order'}>
              <Input type="number" value={form.sortOrder} onChange={(e) => update('sortOrder', parseInt(e.target.value, 10) || 0)}/>
            </Field>
          </div>
          <Field label={isZh ? '值' : 'Value'}>
            <Input value={form.value} onChange={(e) => update('value', e.target.value)} className="font-mono"/>
          </Field>
          <Field label={isZh ? '描述' : 'Description'}>
            <textarea
              className="bg-background border border-border rounded-md text-sm px-3 py-2 w-full"
              rows={3}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
            />
          </Field>
          <Field label={isZh ? '状态' : 'State'}>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active === 1} onChange={(e) => update('active', e.target.checked ? 1 : 0)}/>
              {isZh ? '启用（关闭则不返公开 API）' : 'Active (off = excluded from public API)'}
            </label>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={busy}>{isZh ? '取消' : 'Cancel'}</Button>
          <Button onClick={onSave} disabled={busy}>{busy ? '...' : (isZh ? '保存' : 'Save')}</Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
